import { useState } from "react";
import { HStack, Stack } from "@chakra-ui/react";
import CDS from "design-system";

import useCaminoStore from "stores/caminoStore";
import EmbeddedHtml from "components/DataDisplay/EmbeddedHtml";
import { Heading22 } from "components/Heading";
import useConfirmation from "hooks/useConfirmation";

import FieldRenderer from "../Fields/FieldRenderer";
import { buildAnswerEntries, validateRequired } from "../helpers";
import type { CustomForm, CustomFormAnswerEntry, CustomFormAnswerMap } from "../types";

interface Props {
  form: CustomForm;
  onSubmit: (answers: CustomFormAnswerEntry[]) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

// Shared by the real form-response page and the builder's preview so they can't drift.
export default function FormFillView({ form, onSubmit, isSubmitting = false, onCancel = undefined }: Props) {
  const [values, setValues] = useState<CustomFormAnswerMap>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { confirm, ConfirmationDialog } = useConfirmation();
  const currentUser = useCaminoStore((state) => state.currentUser);
  const submitterEmail = form.showSubmitterInfo ? currentUser?.workEmail : undefined;

  const fields = form.customFormFields ?? [];
  const canSubmit = !isSubmitting && fields.length > 0;

  const attemptSubmit = () => {
    if (!canSubmit) return;
    const validationErrors = validateRequired(fields, values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    confirm({
      header: "Ready to submit?",
      body: (
        <>
          You won&apos;t be able to edit this response after submitting. Make sure your answers are up to date.
          {submitterEmail && (
            <CDS.Text14 mt={3} fontWeight="semibold">
              Submitting as {submitterEmail}
            </CDS.Text14>
          )}
        </>
      ),
      confirmButtonText: "Submit",
      cancelButtonText: "Keep editing",
      onConfirm: () => onSubmit(buildAnswerEntries(fields, values)),
    });
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Heading22 as="h1" fontSize="24px" fontWeight={700}>{form.name}</Heading22>
        {form.description && (
          <EmbeddedHtml innerHtml={form.description} color="fg.subtle" />
        )}
      </Stack>

      <Stack spacing={5}>
        {fields.map((field) => (
          <FieldRenderer
            key={field.uuid}
            field={field}
            mode="submit"
            value={values[field.uuid]}
            error={errors[field.uuid]}
            onChange={(value) => setValues((prev) => ({ ...prev, [field.uuid]: value }))}
          />
        ))}
        <HStack justify="flex-end" mt={2} spacing={3}>
          {submitterEmail && (
            <CDS.Text12 color="fg.subtle">Submitting as {submitterEmail}</CDS.Text12>
          )}
          {onCancel && (
            <CDS.Button intent={CDS.Button.intent.secondary} variant={CDS.Button.variant.ghost} onClick={onCancel}>
              Cancel
            </CDS.Button>
          )}
          <CDS.Button
            intent={CDS.Button.intent.indigo}
            variant={CDS.Button.variant.solid}
            loading={isSubmitting}
            disabled={!canSubmit}
            onClick={attemptSubmit}
          >
            Submit
          </CDS.Button>
        </HStack>
      </Stack>

      <ConfirmationDialog />
    </Stack>
  );
}
